import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, styled } from '@mui/material';
import moment from 'moment';
import { RotationList } from 'stoerk-ui-components';
import {
  RouterComponentProps,
  withRouter,
} from '../../../../../../util/route-dom';
import { PolyglotComponentProps, withPolyglot } from '../../../../../../i18n';
import withHandlingErrors, {
  HandlingErrorWrappedProps,
} from '../../../../../../handlingErrors';
import {
  DeviceControlActionMetadataAPIResponse,
  DeviceControlUnitsAPIResponse,
} from '../../../../../../redux/commander/api/device.model';
import { useCurrentStateByControlUnitId } from '../../../../../../redux/controlUnit/hooks/useCurrentStateByControlUnitId';
import { CU_POLLING_INTERVAL } from '../../../../../../redux/CUActions/api/CUActions.constants';
import Util from '../../../../../../util/Util';
import { useAppSelector } from '../../../../../../redux/store.model';
import { getFeatureToggle } from '../../../../../../featureToggle';
import { getDeviceDataByIdParams } from '../../../../../../redux/commander/selectors';
import useCommanderStatus from '../../../../../../redux/commander/commander-status/hooks/useCommanderStatus';
import DeviceModel from '../../../../../../redux/commander/api/DeviceModel';
import { StatuCercleIcon } from '../../../../../../theme/icons';
import { LightIcon, PowerIcon } from '../../../../theme/icons';
import usePreferredTemperatureUnit from '../../../../../../redux/settings/hooks/usePreferredTemperatureUnit';
import {
  getUnitStrFromSensor,
  getValueStrFromSensor,
} from '../../../../../../redux/controlUnit/api/util/sensor/formatValues';
import ControlUnitGridItem from '../../../../../../components/DeviceManager/ControlUnit/ControlUnitCardGrid/ControlUnitGridItem';
import { DEFAULT_VALUE_SENSOR } from '../../../../../../redux/controlUnit/api/util/constants';
import CUActionsAPI from '../../../../../../redux/CUActions/api/CUActionsAPI';
import useShowErrorMessage from '../../../../../../handlingErrors/useShowErrorMessage';
import { getCUActionsResponseByActionId } from '../../../../../../redux/CUActions/selectors/CUActionsAPISelectors';
import { actionViewMapping } from '../../../../../../redux/controlUnit/api/util/action/actionViewMapping';
import { ActionButtonType } from '../../../../../../theme/components/Device/ActionButton/ActionButtonsConstants';

interface ControlUnitCardGridProps
  extends RouterComponentProps,
    PolyglotComponentProps,
    HandlingErrorWrappedProps {
  controlUnit: DeviceControlUnitsAPIResponse;
  deviceId: string;
  groupId?: string;
}
export function ControlUnitCardGridSimple(props: ControlUnitCardGridProps) {
  const { controlUnit, navigate, deviceId, groupId, polyglot } = props;
  const {
    loading: isLoading,
    currentAlarms,
    hasAlarms,
    isOn,
    mainSensor,
    color,
    colorStatus,
    cloudConnectivity,
    CUTranslate,
  } = useCurrentStateByControlUnitId(deviceId, controlUnit.id);

  const proxyBackendURL = new Util().getCommanderProxyURL();
  const featureToggle = useAppSelector(getFeatureToggle);
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  const device = useAppSelector((state) =>
    getDeviceDataByIdParams(state, { deviceId })
  );

  const showControlUnitsDetailsView = featureToggle?.controlUnitsDetailsView;
  const { commanderStatus } = useCommanderStatus(deviceId);
  const { cloudConnectivityVPN } = new DeviceModel().cloudConnectivityStatus(
    device?.lastPing,
    commanderStatus
  );
  const actionsSupported = useMemo(() => {
    // If multiple Light actions exist, keep only the first one
    const lightActions = controlUnit.actions
      .filter((action) => action.id.search(/Light/i) !== -1)
      .map((action) => ({ ...action, actionType: ActionButtonType.LIGHT }));
    const onAction = controlUnit.actions
      .filter((action) => action.id === 'On')
      .map((action) => ({
        ...action,
        actionType: ActionButtonType.TURN_ON_OFF,
      }));

    if (lightActions.length > 1) {
      return onAction ? [lightActions[0], ...onAction] : [lightActions[0]];
    }
    return onAction ? [...lightActions, ...onAction] : [...lightActions];
  }, [controlUnit.actions, polyglot, CUTranslate]);

  const titleAlarm = hasAlarms ? (
    <RotationList
      list={currentAlarms.map(
        (item) =>
          CUTranslate(item?.name ?? item?.eventID ?? '', 'ram', 'longText') ??
          item?.name ??
          item?.eventID
      )}
    />
  ) : undefined;

  const onClickCard = () => {
    if (showControlUnitsDetailsView) {
      let url = `/devicemanager/device/${deviceId}/device-detail/control-unit/${controlUnit.id}`;
      if (groupId) {
        url = `/devicemanager/device/${deviceId}/group/${groupId}/device-detail/control-unit/${controlUnit.id}`;
      }
      navigate(url);
    } else {
      if (cloudConnectivityVPN) {
        const deviceRedirectURL = `${proxyBackendURL}/${device?.ip}/?token=${accessToken}`;
        window.open(deviceRedirectURL, '_blank');
      }
    }
  };

  let iconsRight: React.ReactNode = (
    <Box color={colorStatus} height={'2em'}>
      <StatuCercleIcon height={'2em'} />
    </Box>
  );

  if (isLoading) {
    iconsRight = (
      <Box color={colorStatus} height={'2em'}>
        <CircularProgress
          sx={{ color: 'primary.contrastText', height: '2em' }}
        />
      </Box>
    );
  }
  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();
  const value = getValueStrFromSensor(
    mainSensor,
    isOn,
    cloudConnectivity,
    polyglot,
    preferredTemperatureUnit
  );
  const unitValue =
    cloudConnectivity &&
    getUnitStrFromSensor(mainSensor, isOn, preferredTemperatureUnit);
  const title = mainSensor?.timestamp
    ? `${polyglot.t('device.controlUnit.value_from')} ${moment(
        mainSensor?.timestamp
      ).fromNow()}`
    : undefined;

  const actionButtons = (
    <>
      {actionsSupported.map((action) => (
        <ActionButton
          key={action.id}
          selectedAction={action}
          deviceId={deviceId}
          deviceOn={!!commanderStatus?.mqttConnected}
          controlUnit={controlUnit}
          hasAlarms={currentAlarms.length > 0}
          isLoading={isLoading}
        />
      ))}
    </>
  );

  return (
    <ControlUnitGridItem
      name={controlUnit.name}
      alarm={titleAlarm}
      color={color}
      icons={iconsRight}
      onClick={onClickCard}
      sensorName={
        (CUTranslate(
          mainSensor?.metadata?.name ?? mainSensor?.channel ?? ''
        ) as string) ?? DEFAULT_VALUE_SENSOR
      }
      value={<span title={title}>{value}</span>}
      unitValue={unitValue}
      isLoading={isLoading}
      actions={actionButtons}
    ></ControlUnitGridItem>
  );
}

const StyleLightButton = styled(Button, {
  shouldForwardProp: (prop: any) =>
    !['isOn', 'deviceOn', 'hasAlarm'].includes(prop),
})<{
  isOn?: boolean;
  deviceOn?: boolean;
  hasAlarm?: boolean;
}>(({ theme, isOn, deviceOn, hasAlarm }) => ({
  px: 3,
  borderRadius: '1.5rem',
  boxShadow: 'none',
  minWidth: '6em',

  // Alarm state (priority)
  ...(hasAlarm &&
    isOn && {
      backgroundColor: '#C79292',
      color: '#713C3C',
      '&:hover': {
        backgroundColor: '#B58080',
      },
    }),
  ...(hasAlarm &&
    !isOn && {
      backgroundColor: '#E68383',
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: '#D47171',
      },
    }),
  // Device ON states
  ...(!hasAlarm &&
    deviceOn &&
    isOn && {
      backgroundColor: '#CBE0F0',
      color: '#006AB3',
      '&:hover': {
        backgroundColor: '#B0D4E8',
      },
    }),
  ...(!hasAlarm &&
    deviceOn &&
    !isOn && {
      backgroundColor: '#A5A3A3',
      color: '#757576',
      '&:hover': {
        backgroundColor: '#B0B0B0',
      },
    }),
  // Device OFF states
  ...(!hasAlarm &&
    !deviceOn &&
    isOn && {
      backgroundColor: '#CBE0F0',
      color: '#006AB3',
      '&:hover': {
        backgroundColor: '#B0D4E8',
      },
    }),
  ...(!hasAlarm &&
    !deviceOn &&
    !isOn && {
      backgroundColor: '#A5A3A3',
      color: '#808080',
      '&:hover': {
        backgroundColor: '#8A8A8B',
      },
    }),
}));

const StyleOnButton = styled(
  Button,

  {
    shouldForwardProp: (prop: any) =>
      !['isOn', 'hasAlarm', 'deviceOn'].includes(prop),
  }
)<{ isOn?: boolean; hasAlarm?: boolean }>(({ theme, isOn, hasAlarm }) => ({
  px: 3,
  borderRadius: '1.5rem',
  boxShadow: 'none',
  marginLeft: 'auto',
  minWidth: '6em',
  // When has alarm (priority)
  ...(hasAlarm && {
    backgroundColor: theme.palette.error.main,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  }),
  // When on (normal state)
  ...(!hasAlarm &&
    isOn && {
      backgroundColor: '#006AB3',
      color: '#FFFFFF',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    }),
  // When off
  ...(!hasAlarm &&
    !isOn && {
      backgroundColor: '#FFFFFF',
      color: '#006AB3',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    }),
}));

const ActionsProperties = {
  [ActionButtonType.TURN_ON_OFF]: {
    button: StyleOnButton,
    icon: PowerIcon,
  },
  [ActionButtonType.LIGHT]: {
    icon: LightIcon,
    button: StyleLightButton,
  },
};

interface ActionButtonProps {
  isLoading: boolean;
  deviceId: string;
  selectedAction: DeviceControlActionMetadataAPIResponse & {
    actionType: ActionButtonType;
  };
  controlUnit: DeviceControlUnitsAPIResponse;
  hasAlarms: boolean;
  deviceOn: boolean;
}
const ActionButton = (props: ActionButtonProps) => {
  const {
    selectedAction,
    deviceId,
    controlUnit,
    hasAlarms,
    isLoading,
    deviceOn,
  } = props;
  // Get action status for On and Light
  const action = useAppSelector((state) =>
    getCUActionsResponseByActionId(state, {
      actionId: selectedAction.id,
      deviceId: deviceId,
      controlUnitId: controlUnit.id,
    })
  );
  const showErrorMessage = useShowErrorMessage();

  const ActionIcon =
    ActionsProperties[
      selectedAction.actionType as keyof typeof ActionsProperties
    ].icon;

  const ActionButtonStyled =
    ActionsProperties[
      selectedAction.actionType as keyof typeof ActionsProperties
    ].button;

  const [triggerAction, { isLoading: isTriggerActionLoading }] =
    CUActionsAPI.useTriggerActionMutation();
  const handleTogglePower = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerAction({
      deviceId,
      controlUnitId: parseInt(controlUnit.id),
      actionId: selectedAction.id,
      value: !action?.data?.value,
    })
      .unwrap()
      .catch((error) => {
        if (error?.message?.startsWith('POLLING_')) {
          showErrorMessage('device.controlUnit.action.error.pooling', {
            variant: 'warning',
          });
        } else {
          showErrorMessage(error);
        }
      });
  };

  return (
    <ActionButtonStyled
      onClick={handleTogglePower}
      disabled={isLoading || isTriggerActionLoading}
      variant="contained"
      size="medium"
      isOn={!!action?.data?.value}
      deviceOn={deviceOn}
      hasAlarm={hasAlarms}
    >
      {isTriggerActionLoading ? (
        <CircularProgress size={24} />
      ) : (
        <ActionIcon height={'1.5em'} />
      )}
    </ActionButtonStyled>
  );
};
export default withHandlingErrors(
  withRouter(withPolyglot(ControlUnitCardGridSimple))
);
